import logo from "./logo.png";
import "./App.css";
import { useState, useEffect, useMemo } from "react";

// Hook personalizado para manejar el carrusel de imágenes de servicios
function useServiceImages(
    folderName,
    imagePattern = "imagen",
    extension = "jpg",
    maxImages = 20,
) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [loadedImages, setLoadedImages] = useState([]);
    const [isFading, setIsFading] = useState(false);

    // Verificar qué imágenes realmente existen
    useEffect(() => {
        const checkImages = async () => {
            const existingImages = [];
            const basePath = `/img/services/${folderName}/`;
            const extensions = ["jpg", "jpeg", "png", "webp"];

            if (folderName === "servicio_familias") {
                // Patrón especial para familias
                for (let i = 1; i <= maxImages; i++) {
                    for (const ext of extensions) {
                        const imgPath = `${basePath}familias${i}.${ext}`;
                        const loaded = await new Promise((resolve) => {
                            const timeout = setTimeout(
                                () => resolve(false),
                                1500,
                            );
                            const img = new Image();
                            img.onload = () => {
                                clearTimeout(timeout);
                                resolve(true);
                            };
                            img.onerror = () => {
                                clearTimeout(timeout);
                                resolve(false);
                            };
                            img.src = imgPath;
                        });
                        if (loaded) {
                            existingImages.push(imgPath);
                            break;
                        }
                    }
                    // Si no encontramos la imagen, detener la búsqueda
                    if (existingImages.length === i - 1) {
                        break;
                    }
                }
            } else {
                // Patrón estándar: imagen1, imagen2, etc.
                for (let i = 1; i <= maxImages; i++) {
                    let found = false;
                    for (const ext of extensions) {
                        const imgPath = `${basePath}${imagePattern}${i}.${ext}`;
                        const loaded = await new Promise((resolve) => {
                            const timeout = setTimeout(
                                () => resolve(false),
                                1500,
                            );
                            const img = new Image();
                            img.onload = () => {
                                clearTimeout(timeout);
                                resolve(true);
                            };
                            img.onerror = () => {
                                clearTimeout(timeout);
                                resolve(false);
                            };
                            img.src = imgPath;
                        });
                        if (loaded) {
                            existingImages.push(imgPath);
                            found = true;
                            break;
                        }
                    }
                    // Si no encontramos la imagen en ninguna extensión, detener
                    if (!found) {
                        break;
                    }
                }
            }

            setLoadedImages(existingImages);
        };

        checkImages();
    }, [folderName, imagePattern, maxImages]);

    // Cambiar imagen automáticamente cada 3 segundos con fade suave
    useEffect(() => {
        if (loadedImages.length > 1) {
            const interval = setInterval(() => {
                // Iniciar fade out
                setIsFading(true);

                // Después de que la imagen se desvanezca, cambiar la imagen
                setTimeout(() => {
                    setCurrentImageIndex(
                        (prevIndex) => (prevIndex + 1) % loadedImages.length,
                    );
                    // Iniciar fade in inmediatamente después
                    setTimeout(() => {
                        setIsFading(false);
                    }, 10);
                }, 800); // Tiempo completo de fade out (800ms)
            }, 3000);

            return () => clearInterval(interval);
        }
    }, [loadedImages.length]);

    return {
        currentImage:
            loadedImages.length > 0 ? loadedImages[currentImageIndex] : null,
        hasImages: loadedImages.length > 0,
        isFading: isFading,
    };
}

function App() {
    // Hooks para cada servicio
    const revitImages = useServiceImages("servicio_revit", "imagen", "jpg");
    const dynamoImages = useServiceImages("servicio_dynamo", "imagen", "jpg");
    const pluginsImages = useServiceImages("servicio_plugins", "imagen", "png");
    const macrosImages = useServiceImages("servicio_macros", "imagen", "jpg");
    const familiasImages = useServiceImages("servicio_familias");

    // Estado para el mensaje de copiado
    const [copied, setCopied] = useState(false);

    // Estado para la sección activa
    const [activeSection, setActiveSection] = useState("inicio");

    // Detectar la sección activa usando Intersection Observer
    useEffect(() => {
        const sections = document.querySelectorAll("section[id]");

        // Función para determinar qué sección está activa basándose en la posición del scroll
        const updateActiveSection = () => {
            const scrollPosition = window.scrollY;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            // Si estamos en la parte superior de la página, siempre mostrar "inicio"
            if (scrollPosition < 100) {
                setActiveSection("inicio");
                return;
            }

            // Si estamos cerca del final de la página, mostrar "contacto"
            if (scrollPosition + windowHeight >= documentHeight - 50) {
                setActiveSection("contacto");
                return;
            }

            let currentSection = "inicio";
            const headerOffset = 150; // Offset para considerar el header

            // Ordenar las secciones por posición
            const sortedSections = Array.from(sections).sort(
                (a, b) => a.offsetTop - b.offsetTop,
            );

            sortedSections.forEach((section) => {
                const sectionTop = section.offsetTop;
                const sectionId = section.id;

                // Verificar si la sección está en el viewport
                if (scrollPosition + headerOffset >= sectionTop - 100) {
                    currentSection = sectionId;
                }
            });

            setActiveSection(currentSection);
        };

        // Establecer "inicio" como activo al cargar
        setActiveSection("inicio");

        // Actualizar al hacer scroll
        window.addEventListener("scroll", updateActiveSection, {
            passive: true,
        });

        // También usar Intersection Observer como respaldo
        const observerOptions = {
            root: null,
            rootMargin: "-150px 0px -40% 0px",
            threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
        };

        const observer = new IntersectionObserver((entries) => {
            const scrollPosition = window.scrollY;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            // Si estamos cerca del final, siempre mostrar contacto
            if (scrollPosition + windowHeight >= documentHeight - 50) {
                setActiveSection("contacto");
                return;
            }

            entries.forEach((entry) => {
                // Solo actualizar si no estamos en la parte superior de la página
                if (
                    scrollPosition > 100 &&
                    entry.isIntersecting &&
                    entry.intersectionRatio > 0.1
                ) {
                    // Priorizar la sección que tiene mayor intersección
                    if (
                        entry.intersectionRatio > 0.25 ||
                        entry.target.id === "contacto"
                    ) {
                        setActiveSection(entry.target.id);
                    }
                }
            });
        }, observerOptions);

        sections.forEach((section) => {
            observer.observe(section);
        });

        return () => {
            window.removeEventListener("scroll", updateActiveSection);
            sections.forEach((section) => {
                observer.unobserve(section);
            });
        };
    }, []);

    // Función para copiar el email al portapapeles
    const copyEmailToClipboard = async () => {
        const email = "contacto@transformabim.com";
        try {
            await navigator.clipboard.writeText(email);
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000); // Ocultar el mensaje después de 2 segundos
        } catch (err) {
            console.error("Error al copiar al portapapeles:", err);
            // Fallback para navegadores que no soportan clipboard API
            const textArea = document.createElement("textarea");
            textArea.value = email;
            textArea.style.position = "fixed";
            textArea.style.opacity = "0";
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand("copy");
                setCopied(true);
                setTimeout(() => {
                    setCopied(false);
                }, 2000);
            } catch (err) {
                console.error("Error al copiar:", err);
            }
            document.body.removeChild(textArea);
        }
    };

    return (
        <div className="App">
            {/* Header */}
            <header className="header">
                <nav className="nav">
                    <div className="nav-logo">
                        <img
                            src={logo}
                            className="logo"
                            alt="TransformaBIM Logo"
                        />
                    </div>
                    <ul className="nav-menu">
                        <li>
                            <a
                                href="#inicio"
                                className={
                                    activeSection === "inicio" ? "active" : ""
                                }
                            >
                                Inicio
                            </a>
                        </li>
                        <li>
                            <a
                                href="#servicios"
                                className={
                                    activeSection === "servicios"
                                        ? "active"
                                        : ""
                                }
                            >
                                Servicios
                            </a>
                        </li>
                        <li>
                            <a
                                href="#contacto"
                                className={
                                    activeSection === "contacto" ? "active" : ""
                                }
                            >
                                Contacto
                            </a>
                        </li>
                    </ul>
                </nav>
            </header>

            {/* Hero Section */}
            <section id="inicio" className="hero">
                <div className="hero-content">
                    <p className="hero-subtitle">
                        Especialistas en transformación digital de proyectos
                        arquitectónicos, estructurales y MEP
                    </p>
                    <p className="hero-description">
                        Convertimos tus proyectos DWG en modelos BIM
                        profesionales con Revit, y desarrollamos herramientas
                        personalizadas para optimizar tu flujo de trabajo.
                    </p>
                </div>
            </section>

            {/* Services Section */}
            <section id="servicios" className="services">
                <div className="container">
                    <h2 className="section-title">Nuestros Servicios</h2>
                    <div className="services-grid">
                        <div className="service-card">
                            <div className="service-card-inner">
                                <div className="service-card-front">
                                    {revitImages.hasImages && (
                                        <img
                                            src={revitImages.currentImage}
                                            alt="Conversión DWG a Modelo 3D"
                                            className={`service-image service-image-carousel ${revitImages.isFading ? "fading" : ""}`}
                                        />
                                    )}
                                    <h3 className="service-title service-title-front">
                                        Conversión DWG a Modelo 3D
                                    </h3>
                                </div>
                                <div className="service-card-back">
                                    <div className="service-card-back-content">
                                        <div className="service-icon">
                                            <img
                                                src="/img/icons/SERVICIO_MODELADO.png"
                                                alt="Servicio Modelado"
                                            />
                                        </div>
                                        <h3 className="service-title">
                                            Conversión DWG a BIM
                                        </h3>
                                        <p className="service-description">
                                            Transformamos tus proyectos DWG en
                                            modelos 3D con metodologia BIM,
                                            utilizando Autodesk Revit.
                                            Garantizamos la máxima fidelidad y
                                            calidad en la conversión de tus
                                            planos técnicos.
                                            <br />
                                            <br />
                                            Servicio disponible en:
                                            <br />
                                            - Arquitectura
                                            <br />
                                            - Estructuras
                                            <br />
                                            - MEP (Mecánica, Eléctrica,
                                            Plomería)
                                            <br />- Detallado de elementos de la
                                            construcción
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="service-card">
                            <div className="service-card-inner">
                                <div className="service-card-front">
                                    {dynamoImages.hasImages && (
                                        <img
                                            src={dynamoImages.currentImage}
                                            alt="Nodos Personalizados para Dynamo"
                                            className={`service-image service-image-carousel ${dynamoImages.isFading ? "fading" : ""}`}
                                        />
                                    )}
                                    <h3 className="service-title service-title-front">
                                        Nodos Personalizados para Dynamo
                                    </h3>
                                </div>
                                <div className="service-card-back">
                                    <div className="service-card-back-content">
                                        <div className="service-icon">
                                            <img
                                                src="/img/icons/SERVICES_NODES.png"
                                                alt="Servicio Nodes"
                                            />
                                        </div>
                                        <h3 className="service-title">
                                            Nodos Personalizados para Dynamo
                                        </h3>
                                        <p className="service-description">
                                            Desarrollamos nodos personalizados
                                            para Dynamo que se adaptan a tus
                                            necesidades específicas. Automatiza
                                            procesos repetitivos y optimiza tu
                                            flujo de trabajo en Revit.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="service-card">
                            <div className="service-card-inner">
                                <div className="service-card-front">
                                    {pluginsImages.hasImages && (
                                        <img
                                            src={pluginsImages.currentImage}
                                            alt="Plugins para Revit"
                                            className={`service-image service-image-carousel ${pluginsImages.isFading ? "fading" : ""}`}
                                        />
                                    )}
                                    <h3 className="service-title service-title-front">
                                        Plugins para Revit
                                    </h3>
                                </div>
                                <div className="service-card-back">
                                    <div className="service-card-back-content">
                                        <div className="service-icon">
                                            <img
                                                src="/img/icons/SERVICES_PLUGINS.png"
                                                alt="Servicio Plugins"
                                            />
                                        </div>
                                        <h3 className="service-title">
                                            Plugins para Revit
                                        </h3>
                                        <p className="service-description">
                                            Creamos plugins personalizados para
                                            Autodesk Revit que extienden las
                                            funcionalidades del software según
                                            tus requerimientos. Soluciones a
                                            medida para tu equipo.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="service-card">
                            <div className="service-card-inner">
                                <div className="service-card-front">
                                    {macrosImages.hasImages && (
                                        <img
                                            src={macrosImages.currentImage}
                                            alt="Macros para AutoCAD"
                                            className={`service-image service-image-carousel ${macrosImages.isFading ? "fading" : ""}`}
                                        />
                                    )}
                                    <h3 className="service-title service-title-front">
                                        Macros para AutoCAD
                                    </h3>
                                </div>
                                <div className="service-card-back">
                                    <div className="service-card-back-content">
                                        <div className="service-icon">
                                            <img
                                                src="/img/icons/SERVICIO_MACROS.jpg"
                                                alt="Servicio Macros"
                                            />
                                        </div>
                                        <h3 className="service-title">
                                            Macros de AutoCAD
                                        </h3>
                                        <p className="service-description">
                                            Desarrollamos macros personalizadas
                                            para AutoCAD que automatizan tareas
                                            específicas, mejorando la
                                            productividad y reduciendo errores
                                            en tus proyectos CAD.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="service-card">
                            <div className="service-card-inner">
                                <div className="service-card-front">
                                    {familiasImages.hasImages && (
                                        <img
                                            src={familiasImages.currentImage}
                                            alt="Generación de Familias"
                                            className={`service-image service-image-carousel ${familiasImages.isFading ? "fading" : ""}`}
                                        />
                                    )}
                                    <h3 className="service-title service-title-front">
                                        Generación de Familias
                                    </h3>
                                </div>
                                <div className="service-card-back">
                                    <div className="service-card-back-content">
                                        <div className="service-icon">
                                            <img
                                                src="/img/icons/SERVICIO_FAMILIAS.png"
                                                alt="Servicio Familias"
                                            />
                                        </div>
                                        <h3 className="service-title">
                                            Generación de Familias LOD 350
                                        </h3>
                                        <p className="service-description">
                                            Creamos familias paramétricas de
                                            Revit con nivel de detalle LOD 350,
                                            incluyendo toda la información
                                            necesaria para documentación técnica
                                            y modelado avanzado. Familias
                                            optimizadas y listas para
                                            producción.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contacto" className="contact">
                <div className="container">
                    <h2 className="section-title">Contacto</h2>
                    <p className="contact-description">
                        ¿Tienes un proyecto en mente? Contáctanos y te
                        ayudaremos a encontrar la mejor solución para tus
                        necesidades.
                    </p>
                    <div className="contact-info">
                        <p>
                            <img
                                src="/img/icons/ICON_MAIL.png"
                                alt="Email"
                                style={{
                                    width: "30px",
                                    height: "30px",
                                    verticalAlign: "middle",
                                    marginRight: "8px",
                                }}
                            />
                            Email:{" "}
                            <span
                                onClick={copyEmailToClipboard}
                                className="email-clickable"
                            >
                                contacto@transformabim.com
                            </span>
                            {copied && (
                                <span
                                    style={{
                                        marginLeft: "10px",
                                        color: "#28a745",
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    ✓ Copiado!
                                </span>
                            )}
                        </p>
                        <p>
                            <img
                                src="/img/icons/ICON_WHATSAPP.png"
                                alt="WhatsApp"
                                style={{
                                    width: "30px",
                                    height: "30px",
                                    verticalAlign: "middle",
                                    marginRight: "8px",
                                }}
                            />{" "}
                            Teléfono: +52 (442) 269 72 18
                        </p>
                        <p>
                            <img
                                src="/img/icons/ICON_MAP.png"
                                alt="Ubicación"
                                style={{
                                    width: "30px",
                                    height: "30px",
                                    verticalAlign: "middle",
                                    marginRight: "8px",
                                }}
                            />{" "}
                            Queretaro, Qro.
                        </p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="container">
                    <p>
                        &copy; {new Date().getFullYear()} TransformaBIM. Todos
                        los derechos reservados.
                    </p>
                </div>
            </footer>
        </div>
    );
}

export default App;
